<template>
    <div class="modal-overlay" @click.self="$emit('close')">
        <div class="modal-content">
            <header class="modal-header">
                <h2>{{ association.name }}</h2>
                <button class="close-btn" @click="$emit('close')">✕</button>
            </header>

            <div class="imageDes">
                <img v-if="association.image" :src="imageSrc" alt="Image association" class="modal-image" />
                <p class="short-desc">{{ association.description }}</p>
            </div>

            <section class="modal-body">
                <div class="info-lists">
                    <div>
                        <h4>Lieux</h4>
                        <span v-for="l in placeTitles" :key="l" class="badge">{{ l }}</span>
                    </div>
                    <div>
                        <h4>Catégories</h4>
                        <span v-for="c in categoryTitles" :key="c" class="badge">{{ c }}</span>
                    </div>
                    <div>
                        <h4>Publics</h4>
                        <span v-for="p in publicTitles" :key="p" class="badge">{{ p }}</span>
                    </div>
                    <div>
                        <h4>Accessibilité</h4>
                        <span v-for="a in accessibilityTitles" :key="a" class="badge">{{ a }}</span>
                    </div>
                    <div>
                        <h4>Contextes d'intervention</h4>
                        <span v-for="co in contextTitles" :key="co" class="badge">{{ co }}</span>
                    </div>
                </div>

                <strong style="color: #D49B3F;">Description</strong>
                <div v-if="association.content" class="full-content" v-html="association.content"></div>

                <div class="map-section">
                    <h4 class="map-title">Où nous trouver</h4>
                    <div ref="mapContainer" class="map-container"></div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});
export default {
    name: 'AssociationModal',
    props: {
        association: { type: Object, required: true }
    },
    computed: {
        imageSrc() {
            const img = this.association.image
            return img?.startsWith('data:image')
                ? img
                : `data:image/png;base64,${img}`
        },
        placesMap() {
            return this.$store.getters.getPlaces.reduce((m, p) => (m[p.id] = p.title, m), {})
        },
        categoryMap() {
            const map = {}
            const flatten = (list) => {
                list.forEach(cat => {
                    map[cat.id] = cat.title
                    if (cat.children && cat.children.length > 0) {
                    flatten(cat.children)
                    }
                })
            }
            flatten(this.$store.getters.getCategories)
            return map
        },
        publicMap() {
            return this.$store.getters.getPublics.reduce((m, p) => (m[p.id] = p.title, m), {})
        },
        accessibilityMap() {
            return this.$store.getters.getAccessibilities
                .reduce((m, a) => (m[a.id] = a.title, m), {})
        },
        contextMap() {
            return this.$store.getters.getContexts
                .reduce((m, cont) => (m[cont.id] = cont.title, m), {})
        },
        placeTitles() {
            return this.association.place.map(id => this.placesMap[id] || 'Inconnu')
        },
        categoryTitles() {
            return this.association.category.map(
                id => this.categoryMap[id] || 'Inconnu'
            )
        },
        publicTitles() {
            return this.association.public.map(id => this.publicMap[id] || 'Inconnu')
        },
        accessibilityTitles() {
            return this.association.accessibility
                .map(id => this.accessibilityMap[id] || 'Inconnu')
        },
        contextTitles() {
            return this.association.context
                .map(id => this.contextMap[id] || 'Inconnu')
        }
    },
    mounted() {
        this.$nextTick(() => {
            const { latitude, longitude } = this.association
            const map = L.map(this.$refs.mapContainer, {
                center: [latitude, longitude],
                zoom: 13,
                scrollWheelZoom: false
            })
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution:
                    '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            }).addTo(map)
            L.marker([latitude, longitude]).addTo(map)
        })
    }
}
</script>

<style scoped>
@keyframes modalIn {
    from {
        opacity: 0;
        transform: scale(0.9) translateY(-10px);
    }

    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: #fff;
    border: 2px solid #D49B3F;
    border-radius: 12px;
    max-width: 800px;
    width: 80%;
    max-height: 85vh;
    overflow-y: auto;
    position: relative;
    text-align: start;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    animation: modalIn 200ms ease-out forwards;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.5rem;
    border-bottom: 4px solid #D49B3F;
}

.modal-header h2 {
    color: white;
    font-size: 1.8rem;
    margin: 0;
}

.close-btn {
    background: #D49B3F;
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 2.2rem;
    height: 2.2rem;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s;
}

.close-btn:hover {
    background: #B67F33;
}

.imageDes {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 1.5rem;
    align-items: center;
    margin: 1.5rem 0;
    padding: 1rem 1rem;
}

.modal-image {
    width: 100%;
    height: auto;
    border-radius: 8px;
    object-fit: cover;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.short-desc {
    color: #1E1C66;
    line-height: 1.5;
    font-size: 1rem;
    font-weight: bold;
}

.modal-body {
    margin-top: 1rem;
    padding: 1rem 1rem;
}

.info-lists {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 1rem;
    margin: 0.75rem 0;
}

.info-lists h4 {
    color: #D49B3F;
    font-size: 1rem;
    margin: 0 0 0.3rem 0;
}

.badge {
    background-color: #167580;
    color: #fff;
    border-radius: 4px;
    padding: 4px 6px;
    margin: 2px 2px 2px 0;
    font-size: 0.75em;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s;
}

.badge:hover {
    transform: scale(1.05);
}

.full-content {
    color: #1E1C66;
    line-height: 1.6;
    margin: 1.5rem 0;
}


.map-container {
    width: 100%;
    height: 200px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.map-section {
    margin-top: 1.5rem;
}

.map-title {
    color: #D49B3F;
    font-size: 1rem;
    margin: 0 0 0.5rem 0;
    font-weight: bold;
}
</style>